import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, ShieldOff, Smartphone } from "lucide-react";

export function MfaCard() {
  const { user, setUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/auth/mfa").then((r) => setStatus(r.data));
  useEffect(() => { load(); }, []);

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      setCode("");
      setSetup(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const begin = async () => {
    const r = await api.post("/auth/mfa/setup");
    setSetup(r.data);
  };
  const enable = () => run(async () => {
    await api.post("/auth/mfa/enable", { code });
    setUser((await api.get("/auth/me")).data);
  }, "Two-step verification is on.");
  const disable = () => run(async () => {
    await api.post("/auth/mfa/disable", { code });
    setUser((await api.get("/auth/me")).data);
  }, "Two-step verification is off.");

  if (!status) return null;
  const strongly = user?.role === "clinician" || user?.is_admin;

  return (
    <div className="warm-card p-6 space-y-4" data-testid="mfa-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          {status.enabled ? <ShieldCheck className="w-6 h-6 text-emerald-700 mt-0.5" /> : <ShieldOff className="w-6 h-6 text-stone-400 mt-0.5" />}
          <div>
            <h3 className="font-display text-lg font-bold text-stone-900">Two-step verification</h3>
            <p className="text-sm text-stone-600 mt-0.5">
              {status.enabled ? "On. Signing in requires a code from your authenticator app." : "Add a 6-digit code from an authenticator app (Google Authenticator, Authy, 1Password) every time you sign in."}
              {!status.enabled && strongly && <span className="block text-amber-800 font-semibold mt-1">Strongly recommended for coaches — you can see member health information.</span>}
            </p>
          </div>
        </div>
        <span data-testid="mfa-status" className={`text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full ${status.enabled ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-600"}`}>{status.enabled ? "On" : "Off"}</span>
      </div>

      {!status.enabled && !setup && (
        <button data-testid="mfa-begin" onClick={begin} className="amber-btn text-sm inline-flex items-center gap-1.5"><Smartphone className="w-4 h-4" /> Set up two-step verification</button>
      )}

      {setup && (
        <div className="grid sm:grid-cols-[auto,1fr] gap-5 items-start rounded-2xl bg-stone-50 p-4" data-testid="mfa-setup">
          <img src={`data:image/png;base64,${setup.qr_png_base64}`} alt="Scan with your authenticator app" className="w-40 h-40 rounded-xl bg-white p-2 border border-stone-200" data-testid="mfa-qr" />
          <div className="space-y-3 text-sm">
            <p className="text-stone-700"><strong>1.</strong> Scan this code with your authenticator app, or enter the key manually:</p>
            <code data-testid="mfa-secret" className="block font-mono text-xs bg-white border border-stone-200 rounded-lg px-3 py-2 break-all select-all">{setup.secret}</code>
            <p className="text-stone-700"><strong>2.</strong> Enter the 6-digit code the app shows to confirm:</p>
            <div className="flex gap-2">
              <input data-testid="mfa-enable-code" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={7} placeholder="123456"
                className="flex-1 px-3 py-2 rounded-xl border border-stone-300 font-mono tracking-widest focus:border-amber-600 outline-none" />
              <button data-testid="mfa-enable" onClick={enable} disabled={busy || code.replace(/\s/g, "").length < 6} className="amber-btn text-sm disabled:opacity-50">Turn on</button>
            </div>
          </div>
        </div>
      )}

      {status.enabled && (
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-stone-100">
          <span className="text-xs text-stone-500">To turn off, enter a current code:</span>
          <input data-testid="mfa-disable-code" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={7} placeholder="123456"
            className="w-32 px-3 py-1.5 rounded-xl border border-stone-300 font-mono text-sm focus:border-amber-600 outline-none" />
          <button data-testid="mfa-disable" onClick={disable} disabled={busy || code.replace(/\s/g, "").length < 6} className="px-4 py-1.5 rounded-full text-xs font-semibold bg-stone-200 text-stone-700 hover:bg-stone-300 disabled:opacity-50">Turn off</button>
        </div>
      )}
    </div>
  );
}
